import { applyPropertiesToPayload, resolveProperties } from '@theme/cart-attribution';

class AtcButtonBlock extends HTMLElement {
  /** @type {HTMLButtonElement} */
  #button;

  connectedCallback() {
    this.#button = /** @type {HTMLButtonElement} */ (this.querySelector('button'));
    if (this.#button) {
      this.#button.addEventListener('click', this.#handleClick);
    }
  }

  disconnectedCallback() {
    if (this.#button) {
      this.#button.removeEventListener('click', this.#handleClick);
    }
  }

  #handleClick = async () => {
    const variantId = this.dataset.variantId;
    if (!variantId || this.#button.disabled || this.#button.dataset.loading) return;

    this.#setLoading(true);

    try {
      const theme = /** @type {{ routes?: { cart_add_url?: string } } | undefined} */ (
        /** @type {any} */ (window).Theme
      );
      const addResponse = await fetch(
        theme?.routes?.cart_add_url ?? '/cart/add.js',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(
            applyPropertiesToPayload({ id: Number(variantId), quantity: 1 }, resolveProperties(this))
          ),
        }
      );

      const addData = await addResponse.json();

      if (addData.status) {
        this.#showError(addData.message || 'Could not add to cart.');
        return;
      }

      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      try {
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: { resource: cart, sourceId: this.id || '', data: { source: 'atc-button-block' } },
          })
        );
      } catch {
        // ignore listener errors
      }

      this.#showSuccess();
    } catch {
      this.#showError('Could not add to cart.');
    } finally {
      this.#setLoading(false);
    }
  };

  /** @param {boolean} loading */
  #setLoading(loading) {
    if (loading) {
      this.#button.dataset.loading = 'true';
      this.#button.disabled = true;
    } else {
      delete this.#button.dataset.loading;
      this.#button.disabled = false;
    }
  }

  #showSuccess() {
    this.#button.dataset.added = 'true';
    setTimeout(() => delete this.#button.dataset.added, 2000);
  }

  /** @param {string} message */
  #showError(message) {
    const existing = this.querySelector('.atc-btn__error');
    if (existing) existing.remove();

    const el = document.createElement('span');
    el.className = 'atc-btn__error';
    el.setAttribute('role', 'alert');
    el.textContent = message;
    this.appendChild(el);

    setTimeout(() => el.remove(), 4000);
  }
}

if (!customElements.get('atc-button-block')) {
  customElements.define('atc-button-block', AtcButtonBlock);
}
